import React from "react";
import { useDispatch } from "react-redux";
import Button from "../Button";
import SimpleModal from "./SimpleModal";
import { toast } from "react-toastify";
import { Copy, Download } from "lucide-react";
import { hideModal } from "../../store/actions/modal";
import {
  Title,
  SubLabel,
  BackupCodeContainer,
  BackupCodeText,
  IconButton,
} from "./styles";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  backupCode: string;
}

const BackupCodeModal: React.FC<Props> = ({ isOpen, toggle, backupCode }) => {
  const dispatch = useDispatch();
  
  const handleCopyBackupCode = () => {
    try {
      navigator.clipboard.writeText(backupCode);
      toast.success("Código copiado al portapapeles");
    } catch (error) {
      toast.error("Error al copiar el código");
    }
  };

  const handleDownloadBackupCode = () => {
    try {
      if (!backupCode) {
        toast.error("No hay código disponible para descargar");
        return;
      }
      
      const fileName = "codigo_de_respaldo_legales.txt";
      const element = document.createElement("a");
      const file = new Blob(
        [
          `Código de Respaldo - Legales\n\nCódigo: ${backupCode}\n\nGuarda este código en un lugar seguro. Lo necesitarás si pierdes el acceso a tu aplicación de autenticación.\n\nGenerado: ${new Date().toLocaleString()}`,
        ],
        { type: "text/plain" }
      );
      element.href = URL.createObjectURL(file);
      element.download = fileName;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      URL.revokeObjectURL(element.href);
      toast.success("Código descargado");
    } catch (error) {
      toast.error("Error al descargar el código");
    }
  };

  const handleFinalize = () => {
    // Dispatch hideModal directly (same as X button)
    dispatch(hideModal());
    // Call toggle callback after
    if (toggle) {
      setTimeout(() => toggle(), 100);
    }
  };

  return (
    <SimpleModal isOpen={isOpen} onClose={toggle} title="" size="md">
      <div style={{ padding: "24px" }} className="modal-content-responsive">
        <Title>Código de respaldo</Title>
        <SubLabel style={{ marginBottom: "16px" }}>
          Este es tu nuevo código de respaldo, el cual reemplaza tu código
          anterior. Guárdalo en un lugar seguro.
        </SubLabel>

        <BackupCodeContainer>
          <BackupCodeText>{backupCode || "Código no disponible"}</BackupCodeText>
          <div>
            <IconButton
              type="button"
              onClick={handleCopyBackupCode}
              title="Copiar código"
            >
              <Copy size={20} color="#0066cc" />
            </IconButton>
            <IconButton
              type="button"
              onClick={handleDownloadBackupCode}
              title="Descargar código"
            >
              <Download size={20} color="#0066cc" />
            </IconButton>
          </div>
        </BackupCodeContainer>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "0 24px 24px",
        }}
        className="modal-footer-responsive"
      >
        <Button
          type="button"
          options={{ skin: "primary", size: "md", type: "filled" }}
          onClick={handleFinalize}
        >
          Finalizar
        </Button>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .modal-content-responsive {
            padding: 16px !important;
          }
          .modal-footer-responsive {
            padding: 0 16px 16px !important;
          }
          .modal-footer-responsive button {
            width: 100%;
          }
        }
      `}</style>
    </SimpleModal>
  );
};

export default BackupCodeModal;
